/**
 * BillingPage
 *
 * Intent component that renders a Stripe pricing table and
 * customer portal link. Registered in the CustomPage intent registry.
 */

'use client';

import React from 'react';
import {
  Card,
  Stack,
  Typography,
  Button,
  PageHeader,
} from '@org/design-system';
import { useBilling } from './billing-context';
import { useAppConfig } from '../config/config-context';

export function BillingPage() {
  const { openCustomerPortal, currentPlan, enabled } = useBilling();
  const { billing } = useAppConfig();

  if (!enabled || !billing) {
    return (
      <Stack gap={6}>
        <PageHeader title="Billing" />
        <Card className="p-6">
          <Typography color="muted">
            Billing is not configured for this application.
          </Typography>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap={6}>
      <PageHeader
        title="Billing"
        description={currentPlan ? `Current plan: ${currentPlan}` : 'Choose a plan to get started'}
        actions={[
          ...(billing.customerPortalUrl
            ? [{
                label: 'Manage Subscription',
                onClick: openCustomerPortal,
                variant: 'outline' as const,
              }]
            : []),
        ]}
      />

      {billing.pricingTableId && billing.publishableKey ? (
        <Card className="overflow-hidden">
          <StripePricingTable
            pricingTableId={billing.pricingTableId}
            publishableKey={billing.publishableKey}
          />
        </Card>
      ) : (
        <Card className="p-6">
          <Stack gap={4} className="text-center">
            <Typography variant="h4">Pricing</Typography>
            <Typography color="muted">
              Contact us for pricing information.
            </Typography>
            {billing.customerPortalUrl && (
              <Button variant="outline" onClick={openCustomerPortal}>
                Manage Existing Subscription
              </Button>
            )}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

// =============================================================================
// Stripe Pricing Table Web Component
// =============================================================================

interface StripePricingTableProps {
  pricingTableId: string;
  publishableKey: string;
}

function StripePricingTable({ pricingTableId, publishableKey }: StripePricingTableProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    // Load Stripe pricing table script if not already loaded
    const scriptId = 'stripe-pricing-table-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://js.stripe.com/v3/pricing-table.js';
      script.async = true;
      document.head.appendChild(script);
    }

    // Create the pricing table web component
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
      const element = document.createElement('stripe-pricing-table');
      element.setAttribute('pricing-table-id', pricingTableId);
      element.setAttribute('publishable-key', publishableKey);
      containerRef.current.appendChild(element);
    }
  }, [pricingTableId, publishableKey]);

  return <div ref={containerRef} className="min-h-[400px]" />;
}
